import { useState, useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { COMPANY_INFO } from '../constants';
import sbinkLogo from '../assets/sbinklogo.png';

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const bounce = keyframes`
  0%, 20%, 53%, 80%, 100% {
    transform: translate3d(0, 0, 0);
  }
  40%, 43% {
    transform: translate3d(0, -10px, 0);
  }
  70% {
    transform: translate3d(0, -5px, 0);
  }
  90% {
    transform: translate3d(0, -2px, 0);
  }
`;

// Styled Components
const WhatsAppContainer = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1000;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
`;

const WhatsAppButton = styled.button`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(37, 211, 102, 0.4);
  transition: all 0.3s ease;
  animation: ${bounce} 2s infinite;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(37, 211, 102, 0.6);
    animation: ${pulse} 1s infinite;
  }
  
  &:active {
    transform: scale(0.95);
  }
  
  svg {
    width: 32px;
    height: 32px;
    fill: white;
  }
`;

const ChatPopup = styled.div`
  position: absolute;
  bottom: 70px;
  right: 0;
  width: 320px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  animation: ${props => props.isVisible ? slideIn : slideOut} 0.3s ease-in-out;
  overflow: hidden;
  
  @media (max-width: 480px) {
    width: 280px;
    right: -10px;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  
  img {
    width: 32px;
    height: 32px;
    object-fit: contain;
  }
`;

const HeaderText = styled.div`
  flex: 1;
  
  h4 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  
  p {
    margin: 2px 0 0 0;
    font-size: 12px;
    opacity: 0.9;
  }
`;

const OnlineStatus = styled.div`
  width: 8px;
  height: 8px;
  background: #4caf50;
  border-radius: 50%;
  animation: ${pulse} 2s infinite;
`;

const ChatBody = styled.div`
  padding: 20px;
  background: #f8f9fa;
`;

const Message = styled.div`
  background: white;
  padding: 12px 16px;
  border-radius: 18px;
  margin-bottom: 16px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border-style: solid;
    border-width: 0 8px 8px 0;
    border-color: transparent white transparent transparent;
  }
  
  p {
    margin: 0;
    color: #333;
    font-size: 14px;
    line-height: 1.4;
  }
`;

const QuickReplies = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
`;

const QuickReply = styled.button`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 8px 16px;
  text-align: left;
  font-size: 13px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f0f0f0;
    border-color: #25d366;
  }
`;

const StartChatButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #25d366 0%, #128c7e 100%);
  color: white;
  border: none;
  border-radius: 25px;
  padding: 12px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-top: 16px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(37, 211, 102, 0.3);
  }
  
  svg {
    width: 18px;
    height: 18px;
    fill: white;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 18px;
  cursor: pointer;
  padding: 4px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const WhatsAppChat = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [autoCloseTimer, setAutoCloseTimer] = useState(null);
  
  // Use ref to track current hover state for timer callbacks
  const isHoveredRef = useRef(false);

  // WhatsApp number (retrieved from company info)
  const whatsappNumber = "27" + COMPANY_INFO.britsWhatsApp.replace(/\s/g, ''); // Remove spaces and add country code
  const defaultMessage = "Hi! I need help with your products/services.";

  const quickReplies = [
    "I need help with pricing",
    "Tell me about your products",
    "I need help with my printer",
    "I have a technical question",
    "I want to place an order"
  ];

  const toggleChat = () => {
    if (isOpen) {
      setIsOpen(false);
      setTimeout(() => setShowPopup(false), 300);
      // Clear any existing auto-close timer
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        setAutoCloseTimer(null);
      }
    } else {
      setShowPopup(true);
      setIsOpen(true);
      
      // Set auto-close timer for manual opens (6 seconds)
      const timer = setTimeout(() => {
        // Use ref to get current hover state
        if (!isHoveredRef.current) {
          setIsOpen(false);
          setTimeout(() => setShowPopup(false), 300);
        }
        setAutoCloseTimer(null);
      }, 6000);
      
      setAutoCloseTimer(timer);
    }
  };

  const openWhatsApp = (message = defaultMessage) => {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
    
    // Clear any existing auto-close timer when user interacts
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }
    
    setIsOpen(false);
    setTimeout(() => setShowPopup(false), 300);
  };

  const handleQuickReply = (reply) => {
    openWhatsApp(reply);
  };

  // Auto-show popup after 5 seconds (only once)
  useEffect(() => {
    if (!hasAutoOpened) {
      const timer = setTimeout(() => {
        if (!isOpen) {
          setShowPopup(true);
          setIsOpen(true);
          setHasAutoOpened(true);
          
          // Auto-hide after 8 seconds if not hovered
          const hideTimer = setTimeout(() => {
            if (!isHoveredRef.current) {
              setIsOpen(false);
              setTimeout(() => setShowPopup(false), 300);
            }
          }, 8000);
          
          setAutoCloseTimer(hideTimer);
        }
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [hasAutoOpened, isOpen]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
      }
    };
  }, [autoCloseTimer]);

  const handleMouseEnter = () => {
    isHoveredRef.current = true;
    // Clear auto-close timer when user hovers
    if (autoCloseTimer) {
      clearTimeout(autoCloseTimer);
      setAutoCloseTimer(null);
    }
  };

  const handleMouseLeave = () => {
    isHoveredRef.current = false;
    // If popup is open and user leaves, close after 3 seconds
    if (isOpen) {
      const timer = setTimeout(() => {
        if (!isHoveredRef.current) {
          setIsOpen(false);
          setTimeout(() => setShowPopup(false), 300);
        }
        setAutoCloseTimer(null);
      }, 3000);
      
      setAutoCloseTimer(timer);
    }
  };

  return (
    <WhatsAppContainer 
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showPopup && (
        <ChatPopup isVisible={isOpen}>
          <ChatHeader>
            <Avatar>
              <img src={sbinkLogo} alt="SB Ink Logo" />
            </Avatar>
            <HeaderText>
              <h4>SB Ink Support</h4>
              <p>We typically reply instantly</p>
            </HeaderText>
            <OnlineStatus />
            <CloseButton onClick={toggleChat}>×</CloseButton>
          </ChatHeader>
          
          <ChatBody>
            <Message>
              <p>Hi there! 👋</p>
              <p>Need help? We&apos;re here to assist you with any questions about our products or services.</p>
            </Message>
            
            <QuickReplies>
              {quickReplies.map((reply, index) => (
                <QuickReply key={index} onClick={() => handleQuickReply(reply)}>
                  {reply}
                </QuickReply>
              ))}
            </QuickReplies>
            
            <StartChatButton onClick={() => openWhatsApp()}>
              <svg viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.653"/>
              </svg>
              Start Chat
            </StartChatButton>
          </ChatBody>
        </ChatPopup>
      )}
      
      <WhatsAppButton onClick={toggleChat}>
        <svg viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.89 3.653"/>
        </svg>
      </WhatsAppButton>
    </WhatsAppContainer>
  );
};

export default WhatsAppChat;
